import React, { useState, useEffect, useCallback } from 'react';

const MathBattleGame = ({ gameSession, onGameComplete, onBack }) => {
  const [startTime] = useState(Date.now());
  const [currentLevel, setCurrentLevel] = useState(1);
  const [playerHealth, setPlayerHealth] = useState(100);
  const [enemyHealth, setEnemyHealth] = useState(100);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [timeLeft, setTimeLeft] = useState(15);
  const [gameOver, setGameOver] = useState(false);
  const [battleLog, setBattleLog] = useState([]);
  const [enemyType, setEnemyType] = useState('goblin');
  
  const enemies = {
    goblin: { name: 'Goblin', emoji: '👹', health: 100, damage: 15 },
    orc: { name: 'Orc', emoji: '👺', health: 120, damage: 20 },
    dragon: { name: 'Dragon', emoji: '🐉', health: 150, damage: 25 },
    wizard: { name: 'Dark Wizard', emoji: '🧙‍♂️', health: 80, damage: 30 }
  };

  // Grade-appropriate difficulty scaling
  const GRADE_BATTLE_MAPPINGS = {
    'Year 3': { range: [1, 20], operations: ['+', '-'], timeLimit: 20, description: 'Addition and subtraction to 20' },
    'Year 4': { range: [1, 100], operations: ['+', '-'], timeLimit: 18, description: 'Addition and subtraction to 100' },
    'Year 5': { range: [1, 100], operations: ['+', '-', '×'], timeLimit: 16, description: 'Operations including multiplication' },
    'Year 6': { range: [1, 200], operations: ['+', '-', '×'], timeLimit: 15, description: 'Extended number range with multiplication' },
    'Year 7': { range: [1, 500], operations: ['+', '-', '×', '÷'], timeLimit: 14, description: 'All operations with larger numbers' },
    'Year 8': { range: [1, 1000], operations: ['+', '-', '×', '÷'], timeLimit: 13, description: 'Complex arithmetic operations' },
    'Year 9': { range: [1, 2000], operations: ['+', '-', '×', '÷'], timeLimit: 12, description: 'Advanced mental arithmetic' },
    'Year 10': { range: [1, 5000], operations: ['+', '-', '×', '÷'], timeLimit: 11, description: 'Senior level arithmetic skills' },
    'Year 11': { range: [1, 10000], operations: ['+', '-', '×', '÷'], timeLimit: 10, description: 'Pre-university arithmetic mastery' },
    'Year 12': { range: [1, 20000], operations: ['+', '-', '×', '÷'], timeLimit: 10, description: 'University-level calculation speed' }
  };

  // Enhance battle settings with grade
  const getGradeEnhancedSettings = (difficulty, gradeLevel) => {
    const gradeMapping = GRADE_BATTLE_MAPPINGS[gradeLevel];
    if (!gradeMapping) return null; // Use traditional settings

    const baseRange = gradeMapping.range;
    const operations = gradeMapping.operations;

    // Scale based on difficulty while respecting grade limits
    let adjustedRange, selectedOperations;
    switch (difficulty) {
      case 'easy':
        adjustedRange = [baseRange[0], Math.floor(baseRange[1] * 0.5)];
        selectedOperations = operations.slice(0, Math.max(1, operations.length - 1));
        break;
      case 'medium':
        adjustedRange = baseRange;
        selectedOperations = operations;
        break;
      case 'hard':
        adjustedRange = [baseRange[0], Math.floor(baseRange[1] * 1.5)];
        selectedOperations = operations;
        break;
      default:
        adjustedRange = baseRange;
        selectedOperations = operations;
    }

    return {
      range: adjustedRange,
      operations: selectedOperations,
      timeLimit: gradeMapping.timeLimit,
      gradeInfo: gradeMapping.description
    };
  };

  const generateQuestion = useCallback(() => {
    const difficulty = gameSession?.difficulty || 'easy';
    const gradeLevel = gameSession?.grade_level;
    let num1, num2, operation, answer, options;

    // Try to use grade-enhanced settings first
    const gradeSettings = getGradeEnhancedSettings(difficulty, gradeLevel);

    if (gradeSettings) {
      // Use grade-appropriate settings
      const [minRange, maxRange] = gradeSettings.range;
      const operations = gradeSettings.operations;

      num1 = Math.floor(Math.random() * (maxRange - minRange + 1)) + minRange;
      num2 = Math.floor(Math.random() * (maxRange - minRange + 1)) + minRange;
      operation = operations[Math.floor(Math.random() * operations.length)];
    } else {
      // Fallback to traditional settings for users without grade
      if (difficulty === 'easy') {
        num1 = Math.floor(Math.random() * 12) + 1;
        num2 = Math.floor(Math.random() * 12) + 1;
        operation = Math.random() > 0.5 ? '+' : '×';
      } else if (difficulty === 'medium') {
        num1 = Math.floor(Math.random() * 25) + 5;
        num2 = Math.floor(Math.random() * 15) + 2;
        operation = ['+', '-', '×'][Math.floor(Math.random() * 3)];
      } else {
        num1 = Math.floor(Math.random() * 50) + 10;
        num2 = Math.floor(Math.random() * 20) + 2;
        operation = ['+', '-', '×', '÷'][Math.floor(Math.random() * 4)];
      }
    }

    switch (operation) {
      case '+':
        answer = num1 + num2;
        break;
      case '-':
        answer = Math.abs(num1 - num2);
        if (num1 < num2) [num1, num2] = [num2, num1];
        break;
      case '×':
        answer = num1 * num2;
        break;
      case '÷':
        answer = num1;
        num1 = answer * num2;
        break;
      default:
        answer = num1 + num2;
    }

    // Generate wrong options
    const wrongOptions = new Set();
    while (wrongOptions.size < 3) {
      let wrongAnswer;
      if (operation === '×') {
        wrongAnswer = answer + (Math.floor(Math.random() * 20) - 10);
      } else {
        wrongAnswer = answer + (Math.floor(Math.random() * 10) - 5);
      }
      if (wrongAnswer !== answer && wrongAnswer > 0) {
        wrongOptions.add(wrongAnswer);
      }
    }

    options = [answer, ...Array.from(wrongOptions)];
    
    // Shuffle options
    for (let i = options.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [options[i], options[j]] = [options[j], options[i]];
    }

    setCurrentQuestion({
      text: `${num1} ${operation} ${num2} = ?`,
      options: options,
      correctAnswer: answer,
      num1,
      num2,
      operation
    });

    // Set grade-appropriate time limit
    const timingSettings = getGradeEnhancedSettings(gameSession?.difficulty || 'easy', gameSession?.grade_level);
    const timeLimit = timingSettings ? timingSettings.timeLimit : 15;
    setTimeLeft(timeLimit);
  }, [gameSession]);

  const addToBattleLog = (message, type = 'info') => {
    setBattleLog(prev => [...prev.slice(-4), { message, type, timestamp: Date.now() }]);
  };

  const handleAnswer = (answer) => {
    setSelectedAnswer(answer);
    setShowResult(true);
    
    const isCorrect = answer === currentQuestion.correctAnswer;
    setQuestionsAnswered(prev => prev + 1);
    
    if (isCorrect) {
      setCorrectAnswers(prev => prev + 1);
      setScore(prev => prev + 10);
      
      // Player attacks enemy
      const damage = Math.floor(Math.random() * 30) + 20;
      setEnemyHealth(prev => {
        const newHealth = Math.max(0, prev - damage);
        addToBattleLog(`⚔️ You dealt ${damage} damage to the ${enemies[enemyType].name}!`, 'success');
        return newHealth;
      });
    } else {
      // Enemy attacks player
      const damage = enemies[enemyType].damage;
      setPlayerHealth(prev => {
        const newHealth = Math.max(0, prev - damage);
        addToBattleLog(`💥 The ${enemies[enemyType].name} dealt ${damage} damage to you!`, 'danger');
        return newHealth;
      });
    }

    setTimeout(() => {
      setShowResult(false);
      setSelectedAnswer(null);
      
      // Check win/lose conditions
      if (enemyHealth <= 0 || (isCorrect && enemyHealth - 30 <= 0)) {
        setCurrentLevel(prev => prev + 1);
        setEnemyHealth(100 + (currentLevel * 20));
        const enemyTypes = Object.keys(enemies);
        setEnemyType(enemyTypes[currentLevel % enemyTypes.length]);
        addToBattleLog(`🎉 Level ${currentLevel} complete! Facing new enemy...`, 'success');
      }
      
      if (playerHealth <= 0 || (enemyHealth <= 0 && currentLevel >= 5) || questionsAnswered >= 20) {
        setGameOver(true);
        const finalScore = Math.round((correctAnswers / questionsAnswered) * 100) || 0;
        
        onGameComplete({
          score: finalScore,
          questionsAttempted: questionsAnswered,
          correctAnswers: correctAnswers,
          timeTaken: Math.floor((Date.now() - startTime) / 1000),
          gameData: {
            levelsCompleted: currentLevel,
            finalPlayerHealth: playerHealth,
            battleLog: battleLog
          }
        });
        return;
      }
      
      generateQuestion();
    }, 2000);
  };

  useEffect(() => {
    generateQuestion();
    addToBattleLog(`🏁 Battle begins! Defeat the ${enemies[enemyType].name}!`, 'info');
  }, [generateQuestion, enemyType]);

  useEffect(() => {
    if (timeLeft > 0 && !showResult && !gameOver) {
      const timer = setTimeout(() => setTimeLeft(prev => prev - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !showResult) {
      handleAnswer(-1); // Wrong answer for timeout
    }
  }, [timeLeft, showResult, gameOver]);

  if (gameOver) {
    return (
      <div className="text-center py-8">
        <div className="text-6xl mb-4">
          {playerHealth > 0 ? '🏆' : '💀'}
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-4">
          {playerHealth > 0 ? 'Victory!' : 'Game Over'}
        </h2>
        <p className="text-gray-600 mb-6">
          Level {currentLevel} • {correctAnswers}/{questionsAnswered} correct
        </p>
        <button
          onClick={onBack}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg"
        >
          Back to Games
        </button>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Game Header */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">Math Battle</h2>
            <p className="text-gray-600">Level {currentLevel}</p>
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-600">Score: {score}</p>
            <p className="text-sm text-gray-600">Time: {timeLeft}s</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Player Stats */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="text-center mb-4">
            <div className="text-4xl mb-2">🛡️</div>
            <h3 className="font-bold text-gray-800">You</h3>
          </div>
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span>Health</span>
              <span>{playerHealth}/100</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="bg-green-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${playerHealth}%` }}
              ></div>
            </div>
          </div>
          <div className="text-sm text-gray-600">
            <p>Correct: {correctAnswers}</p>
            <p>Total: {questionsAnswered}</p>
          </div>
        </div>

        {/* Question Area */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="text-center mb-6">
            <div className="text-3xl font-bold text-blue-600 mb-4">
              {currentQuestion?.text}
            </div>
            <div className={`inline-block px-3 py-1 rounded-full text-white text-sm ${
              timeLeft > 10 ? 'bg-green-500' : timeLeft > 5 ? 'bg-yellow-500' : 'bg-red-500'
            }`}>
              {timeLeft}s
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {currentQuestion?.options.map((option, index) => (
              <button
                key={index}
                onClick={() => handleAnswer(option)}
                disabled={showResult}
                className={`p-4 rounded-lg border-2 font-bold text-lg transition-all ${
                  showResult && option === currentQuestion.correctAnswer
                    ? 'bg-green-100 border-green-500 text-green-700'
                    : showResult && selectedAnswer === option && option !== currentQuestion.correctAnswer
                    ? 'bg-red-100 border-red-500 text-red-700'
                    : 'bg-gray-50 border-gray-200 hover:border-blue-500 hover:bg-blue-50'
                }`}
              >
                {option}
              </button>
            ))}
          </div>
        </div>

        {/* Enemy Stats */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <div className="text-center mb-4">
            <div className="text-4xl mb-2">{enemies[enemyType].emoji}</div>
            <h3 className="font-bold text-gray-800">{enemies[enemyType].name}</h3>
          </div>
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span>Health</span>
              <span>{enemyHealth}/{100 + (currentLevel - 1) * 20}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div 
                className="bg-red-500 h-3 rounded-full transition-all duration-500"
                style={{ width: `${(enemyHealth / (100 + (currentLevel - 1) * 20)) * 100}%` }}
              ></div>
            </div>
          </div>
          <div className="text-sm text-gray-600">
            <p>Attack: {enemies[enemyType].damage}</p>
          </div>
        </div>
      </div>

      {/* Battle Log */}
      <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
        <h3 className="font-bold text-gray-800 mb-4">Battle Log</h3>
        <div className="space-y-2 max-h-32 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-300">
          {battleLog.map((log, index) => (
            <div key={log.timestamp} className={`text-sm ${
              log.type === 'success' ? 'text-green-600' : 
              log.type === 'danger' ? 'text-red-600' : 'text-gray-600'
            }`}>
              {log.message}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default MathBattleGame;